import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useProVenueInstances } from "@/hooks/use-provenue-instances"
import type { SmithersProVenueSelection } from "@/types/provenue-instance"
import type { SmithersGlobalConfig } from "@/types/smithers-global"

interface SmithersProVenueFieldProps {
  value: SmithersProVenueSelection
  onChange: (selection: SmithersProVenueSelection) => void
  disabled?: boolean
  globalConfig?: SmithersGlobalConfig
}

export function SmithersProVenueField({ value, onChange, disabled = false, globalConfig }: SmithersProVenueFieldProps) {
  const { data: instances = [], error, isLoading, mutate } = useProVenueInstances("smithers")
  const selected = value.mode === "instance" ? instances.find((instance) => instance.id === value.id) : undefined
  const preview = value.mode === "global" ? globalConfig?.proVenueInstances[0] : selected
  const noInstance = value.mode === "none" || (value.mode === "global" && globalConfig?.proVenueInstanceId === null)
  const unavailableSelection = value.mode === "instance" && !selected
  const useDefault = value.mode === "global"
  const selectionValue = value.mode === "instance" ? String(value.id) : value.mode === "none" ? "none" : ""
  const defaultLabel = globalConfig ? globalConfig.proVenueInstanceId === null ? "None" : globalConfig.proVenueUser || `Instance (${globalConfig.proVenueInstanceId})` : "Default instance"

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label htmlFor="smithers-provenue-instance">ProVenue Instance</Label>
        <Select
          disabled={disabled || useDefault}
          value={selectionValue}
          onValueChange={(next) => {
            if (next === "none") onChange({ mode: "none" })
            else if (instances.some((instance) => instance.id === Number(next))) {
              onChange({ mode: "instance", id: Number(next) })
            }
          }}
        >
          <SelectTrigger id="smithers-provenue-instance"><SelectValue placeholder={useDefault ? defaultLabel : "Select an instance…"} /></SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="none">None</SelectItem>
              {instances.map((instance) => (
                <SelectItem key={instance.id} value={String(instance.id)}>{instance.name}</SelectItem>
              ))}
              {unavailableSelection && value.mode === "instance" && (
                <SelectItem value={String(value.id)} disabled>Unavailable instance ({value.id})</SelectItem>
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Checkbox
            id="smithers-provenue-default"
            checked={useDefault}
            disabled={disabled}
            aria-label="Use default for ProVenue Instance"
            onCheckedChange={(checked) => onChange(checked === true ? { mode: "global" }
              : globalConfig?.proVenueInstanceId != null ? { mode: "instance", id: globalConfig.proVenueInstanceId } : { mode: "none" })}
          />
          <label htmlFor="smithers-provenue-default" className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground">Use default</label>
        </div>
        {isLoading && <p role="status" className="text-sm text-muted-foreground">Loading ProVenue instances…</p>}
        {!isLoading && !error && instances.length === 0 && (
          <p className="text-sm text-muted-foreground">No shared ProVenue instances are available.</p>
        )}
      </div>
      {error && (
        <Alert variant="destructive" className="md:col-span-2">
          <AlertTitle>Couldn’t load ProVenue instances</AlertTitle>
          <AlertDescription>
            <p>The instance list is unavailable. You can still use the default or choose None.</p>
            <Button type="button" variant="outline" disabled={disabled} onClick={() => void mutate()}>Retry instances</Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor="smithers-proVenueUser">ProVenue User</Label>
        <Input id="smithers-proVenueUser" readOnly value={preview?.name ?? ""} placeholder={noInstance ? "No instance" : "Provided by the selected instance"} />
      </div>
      <div className="flex flex-col gap-2 md:col-span-2">
        <Label htmlFor="smithers-proVenueInstances">ProVenue Instances</Label>
        <Input
          id="smithers-proVenueInstances"
          readOnly
          value={preview ? `${preview.name} — ${preview.hostName}` : ""}
          placeholder={noInstance ? "No instance" : "Instance name and host provided by the service"}
        />
      </div>
    </>
  )
}
